import React, { useState, useEffect, useCallback } from 'react';
import { Plus, MoreVertical, Calendar, User, GripVertical, X, Check } from 'lucide-react';
import { teamService } from '../lib/services';
import type { Task, TaskStatus, KanbanBoard } from '../types/api';

interface KanbanBoardProps {
    teamId: string;
}

const COLUMNS: { id: TaskStatus; label: string; color: string }[] = [
    { id: 'TODO', label: 'To Do', color: 'border-slate-500' },
    { id: 'IN_PROGRESS', label: 'In Progress', color: 'border-hig-accent' },
    { id: 'IN_REVIEW', label: 'In Review', color: 'border-yellow-500' },
    { id: 'DONE', label: 'Done', color: 'border-green-500' },
];

export default function KanbanBoardComponent({ teamId }: KanbanBoardProps) {
    const [kanban, setKanban] = useState<KanbanBoard | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showNewTask, setShowNewTask] = useState<TaskStatus | null>(null);
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [draggedTask, setDraggedTask] = useState<Task | null>(null);

    const loadTasks = useCallback(async () => {
        setIsLoading(true);
        const response = await teamService.getTasks(teamId);
        if (response.data) {
            setKanban(response.data.kanban);
        }
        setIsLoading(false);
    }, [teamId]);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    const handleCreateTask = async (status: TaskStatus) => {
        if (!newTaskTitle.trim()) return;

        await teamService.createTask(teamId, {
            title: newTaskTitle.trim(),
            status,
        });

        setNewTaskTitle('');
        setShowNewTask(null);
        loadTasks();
    };

    const handleMoveTask = async (task: Task, newStatus: TaskStatus) => {
        if (task.status === newStatus) return;

        await teamService.updateTask(teamId, task.id, { status: newStatus });
        loadTasks();
    };

    const handleDragStart = (e: React.DragEvent, task: Task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e: React.DragEvent, status: TaskStatus) => {
        e.preventDefault();
        if (draggedTask) {
            handleMoveTask(draggedTask, status);
            setDraggedTask(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex gap-4 overflow-x-auto pb-4">
            {COLUMNS.map((column) => {
                const tasks = kanban?.[column.id] || [];
                const isAddingTask = showNewTask === column.id;

                return (
                    <div
                        key={column.id}
                        className="flex-shrink-0 w-72 bg-slate-800/50 rounded-xl"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, column.id)}
                    >
                        {/* Column Header */}
                        <div className={`p-3 border-b-2 ${column.color} rounded-t-xl`}>
                            <div className="flex items-center justify-between">
                                <h3 className="font-medium text-white">{column.label}</h3>
                                <span className="text-sm text-slate-400 bg-slate-700 px-2 py-0.5 rounded">
                                    {tasks.length}
                                </span>
                            </div>
                        </div>

                        {/* Tasks */}
                        <div className="p-2 space-y-2 min-h-[200px]">
                            {tasks.map((task) => (
                                <div
                                    key={task.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, task)}
                                    className="bg-slate-700 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:bg-slate-600/80 transition-colors group"
                                >
                                    <div className="flex items-start gap-2">
                                        <GripVertical size={14} className="text-slate-500 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-white">{task.title}</p>
                                            {task.dueDate && (
                                                <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                                                    <Calendar size={12} />
                                                    <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                                                </div>
                                            )}
                                        </div>
                                        <button className="p-1 text-slate-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                            <MoreVertical size={14} />
                                        </button>
                                    </div>
                                    {task.assigneeId && (
                                        <div className="mt-2 flex items-center gap-1">
                                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                                <User size={10} className="text-white" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Add Task Form */}
                            {isAddingTask ? (
                                <div className="bg-slate-700 rounded-lg p-3">
                                    <input
                                        type="text"
                                        value={newTaskTitle}
                                        onChange={(e) => setNewTaskTitle(e.target.value)}
                                        placeholder="Enter task title..."
                                        className="w-full bg-transparent border-none outline-none text-sm text-white placeholder-slate-400"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleCreateTask(column.id);
                                            if (e.key === 'Escape') {
                                                setShowNewTask(null);
                                                setNewTaskTitle('');
                                            }
                                        }}
                                    />
                                    <div className="flex items-center gap-2 mt-2">
                                        <button
                                            onClick={() => handleCreateTask(column.id)}
                                            className="p-1.5 bg-sky-500 hover:bg-sky-400 rounded text-white"
                                        >
                                            <Check size={14} />
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowNewTask(null);
                                                setNewTaskTitle('');
                                            }}
                                            className="p-1.5 bg-slate-600 hover:bg-slate-500 rounded text-white"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setShowNewTask(column.id)}
                                    className="w-full p-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg flex items-center gap-2 transition-colors"
                                >
                                    <Plus size={14} />
                                    Add task
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
